import React, { useState, useEffect } from "react";
import { Card, Form, Row, Col, Select, Button, Spin, DatePicker, message } from "antd";
import { useNavigate } from "react-router-dom";
import { errHandler } from "../../helper/helper";
import { generateUserLoginDetailsReport, getAllUsersByBranch } from "./GeneralService";

const { Option } = Select;

const UserLoginDetailsReport = ({ loginPlaceId, districtData, districtName, divisionalId, branchId }) => {
    const navigate = useNavigate();
    const [selectedDistrict, setSelectedDistrict] = useState(null);
    const [selectedDivisional, setSelectedDivisional] = useState(null);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [loading, setLoading] = useState(false);

    const [availableDivisionalAreas, setAvailableDivisionalAreas] = useState([]);
    const [availableBranches, setAvailableBranches] = useState([]);
    const [availableUsers, setAvailableUsers] = useState([]);

    const [initialDivisionalId] = useState(divisionalId || null);
    const [initialDistrictName] = useState(districtName || null);

    const fixedBranchName = loginPlaceId === "1" && branchId
        ? (() => {
            for (const d of districtData || []) {
                for (const div of d.divisionalAreaDtoList || []) {
                    const b = (div.branchDtoList || []).find(b => b.id === branchId);
                    if (b) return b.branchName;
                }
            }
            return "Branch";
        })()
        : null;

    useEffect(() => {
        if (loginPlaceId === "1" && branchId && districtData) {
            for (const d of districtData || []) {
                for (const div of d.divisionalAreaDtoList || []) {
                    for (const b of div.branchDtoList || []) {
                        if (b.id === branchId) {
                            setAvailableBranches([b]);
                            setSelectedBranch(branchId);
                            return;
                        }
                    }
                }
            }
        }
    }, [districtData, loginPlaceId, branchId]);

    useEffect(() => {
        if (loginPlaceId === "1") return;
        if (initialDistrictName && loginPlaceId === "4") {
            const district = districtData.find(d => d.districtName === initialDistrictName);
            if (district) setSelectedDistrict(district.districtId);
        }
    }, [initialDistrictName, loginPlaceId, districtData]);

    useEffect(() => {
        if (loginPlaceId === "1") return;
        let filtered = [];
        if (loginPlaceId === "2" && initialDivisionalId) {
            filtered = districtData
                .flatMap(d => d.divisionalAreaDtoList || [])
                .filter(div => div.divAId === initialDivisionalId);
            setSelectedDivisional(initialDivisionalId);
        } else {
            filtered = districtData
                .filter(d => d.districtId === selectedDistrict)
                .flatMap(d => d.divisionalAreaDtoList || []);
        }
        setAvailableDivisionalAreas(filtered);
        if (!filtered.some(d => d.divAId === selectedDivisional)) setSelectedDivisional(null);
        setSelectedBranch(null);
        setSelectedUsers([]);
        setAvailableBranches([]);
        setAvailableUsers([]);
    }, [selectedDistrict, districtData, loginPlaceId, initialDivisionalId]);

    useEffect(() => {
        if (loginPlaceId === "1") return;
        if (!selectedDivisional) {
            setAvailableBranches([]);
            setSelectedBranch(null);
            setSelectedUsers([]);
            setAvailableUsers([]);
            return;
        }
        const divArea = availableDivisionalAreas.find(d => d.divAId === selectedDivisional);
        const branches = divArea ? (divArea.branchDtoList || []) : [];
        setAvailableBranches(branches);
        if (!branches.some(b => b.id === selectedBranch)) setSelectedBranch(null);
            setSelectedUsers([]);
            setAvailableUsers([]);
    }, [selectedDivisional, availableDivisionalAreas]);

    useEffect(() => {
        if (!selectedBranch) {
            setAvailableUsers([]);
            setSelectedUsers([]);
            return;
        }
        const fetchUsers = async () => {
            try {
                const res = await getAllUsersByBranch(selectedBranch);
                if (res?.data) {
                    setAvailableUsers(res.data);
                } else {
                    setAvailableUsers([]);
                }
            } catch (e) {
                setAvailableUsers([]);
            }
            setSelectedUsers([]);
        };
        fetchUsers();
    }, [selectedBranch]);

    const isGenerateDisabled = !(fromDate && toDate);

    const generateReport = async () => {
        setLoading(true);
        try {
            const payload = {
                userIdList: selectedUsers.length > 0 ? selectedUsers : null,
                branchIdList: selectedBranch ? [selectedBranch] : null,
                fromDate: fromDate ? fromDate.format("YYYY-MM-DD") : null,
                toDate: toDate ? toDate.format("YYYY-MM-DD") : null,
            };

            const response = await generateUserLoginDetailsReport(payload);
            const rawData = Array.isArray(response) ? response : [];

            if (rawData.length === 0) {
                message.warning("No data found for the selected parameters");
                return;
            }

            const dateRange = `${fromDate.format("YYYY-MM-DD")} to ${toDate.format("YYYY-MM-DD")}`;
            const reportTitle = `User Login Details Report - ${dateRange}`;
            navigate("user-login-details", { state: { tableData: rawData, reportTitle } });
        } catch (error) {
            console.error(error);
            errHandler(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="panel-container">
            <Card hoverable className="inner-card">
                <Form layout="vertical">
                    <Row gutter={[16, 16]}>
                        {(loginPlaceId === "3" || loginPlaceId === "4") && (
                            <Col span={24}>
                                <Form.Item label="District">
                                    <Select
                                        value={selectedDistrict}
                                        onChange={setSelectedDistrict}
                                        disabled={loginPlaceId === "4"}
                                        allowClear showSearch
                                        filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                                        placeholder="Select District"
                                    >
                                        {districtData.map(d => (
                                            <Option key={d.districtId} value={d.districtId}>{d.districtName}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}
                        {(loginPlaceId === "3" || loginPlaceId === "4" || loginPlaceId === "2") && (
                            <Col span={24}>
                                <Form.Item label="Divisional Area">
                                    <Select
                                        value={selectedDivisional}
                                        onChange={setSelectedDivisional}
                                        disabled={loginPlaceId === "2"}
                                        allowClear showSearch
                                        filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                                        placeholder="Select Divisional"
                                    >
                                        {availableDivisionalAreas.map(d => (
                                            <Option key={d.divAId} value={d.divAId}>{d.divAIdName}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}
                        {(loginPlaceId === "3" || loginPlaceId === "4" || loginPlaceId === "2" || loginPlaceId === "1") && (
                            <Col span={24}>
                                <Form.Item label="Branch">
                                    <Select
                                        value={selectedBranch}
                                        onChange={setSelectedBranch}
                                        allowClear showSearch
                                        filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                                        placeholder={loginPlaceId === "1" ? fixedBranchName : "Select Branch"}
                                        disabled={loginPlaceId === "1" || !availableBranches.length}
                                    >
                                        {availableBranches.map(b => (
                                            <Option key={b.id} value={b.id}>{b.branchName}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}
                        <Col span={24}>
                            <Form.Item label="Users">
                                <Select
                                    mode="multiple"
                                    value={selectedUsers}
                                    onChange={setSelectedUsers}
                                    allowClear showSearch
                                    maxTagCount="responsive"
                                    filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                                    placeholder="Select Users"
                                    disabled={!availableUsers.length}
                                >
                                    {availableUsers.map(u => (
                                        <Option key={u.id} value={u.id}>{u.name}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]}>
                        <Col span={12}>
                            <Form.Item label="From Date">
                                <DatePicker
                                    style={{ width: "100%" }}
                                    value={fromDate}
                                    onChange={(date) => setFromDate(date)}
                                    format="YYYY-MM-DD"
                                    placeholder="Select start date"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="To Date">
                                <DatePicker
                                    style={{ width: "100%" }}
                                    value={toDate}
                                    onChange={(date) => setToDate(date)}
                                    format="YYYY-MM-DD"
                                    placeholder="Select end date"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Form.Item className="generate-btn-outline">
                                <Button
                                    type="primary"
                                    onClick={generateReport}
                                    disabled={loading || isGenerateDisabled}
                                    className="generate-btn"
                                    size="large"
                                    block
                                >
                                    {loading ? <Spin /> : "Generate Report"}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>
        </div>
    );
};

export default UserLoginDetailsReport;